import React from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '@packages/ui/layout';
import type { ProfileMenuItem } from '@packages/ui/layout';
import CheckPermissions from '@/utils/permission/checkPermission';

interface ProfileMenuRendererProps {
  items: ProfileMenuItem[];
  /** Display name shown in the trigger button. */
  userName: string;
}

/**
 * Generic profile dropdown renderer.
 * Iterates over ProfileMenuItem[], renders each with a Link or span,
 * and wraps in CheckPermissions when an item declares a permission.
 */
const ProfileMenuRenderer: React.FC<ProfileMenuRendererProps> = ({ items, userName }) => {
  return (
    <Navbar.ProfileMenu
      content={
        <>
          {items.filter((item) => item.visible).map((item) => {
            const menuItem = item.path ? (
              <Navbar.ProfileMenuItem
                key={item.id}
                variant={item.variant}
                onClick={item.onClick}
                semanticId={`${item.id}ProfileItem`}
              >
                <Link to={item.path}>{item.label}</Link>
              </Navbar.ProfileMenuItem>
            ) : (
              <Navbar.ProfileMenuItem
                key={item.id}
                onClick={item.onClick}
                variant={item.variant}
                semanticId={`${item.id}ProfileItem`}
              >
                <span>{item.label}</span>
              </Navbar.ProfileMenuItem>
            );

            if (item.permission) {
              return (
                <CheckPermissions
                  key={item.id}
                  type={item.permission.type}
                  urlName={item.permission.urlName}
                >
                  {menuItem}
                </CheckPermissions>
              );
            }

            return menuItem;
          })}
        </>
      }
      semanticId="profileMenu"
    >
      <Navbar.UserTrigger name={userName} semanticId="userTrigger" />
    </Navbar.ProfileMenu>
  );
};

export { ProfileMenuRenderer };
